import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import TextInputComponent from '@components/textInput/TextInput'
import SegmentedControl, { Option } from '@components/segmentedControl/SegmentedControl'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import SiteIcon from '@components/icon/SiteIcons'
import Drawer from '@components/drawer/Drawer'
import Icon from '@components/icon/Icons'

import UserCard from '@components/card/UserCard'

import { filterUsers } from '@utils/admin'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'
import { AuthScope } from '@api/enum'

import api from '@api/api'
import Logger from '@utils/logger'
import Button from '@components/button/Button'

interface Props {
  prestataireId: string
}

interface UserSection {
  label: string
  value: string
  users: UserResponse[]
}

interface PrestaSections {
  chef: UserSection
  technician: UserSection
}

const MAIN_VERTICAL_PADDING = 24

const AdminServiceScreen = ({ prestataireId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const services = useReducer(AdminStore.store, (s) => s.services)
  const sites = useReducer(SiteStore.store, (s) => s.sites!)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const canAdd =
    scopes.includes(AuthScope.AdminUtilisateursCreationTous) ||
    scopes.includes(AuthScope.AdminUtilisateursCreationPrestataire)

  const service = React.useMemo(
    () => services.find((service) => service.id === prestataireId),
    [services, prestataireId]
  )

  const [search, setSearch] = React.useState('')
  const [users, setUsers] = React.useState<UserResponse[]>([])
  const [status, setStatus] = React.useState<Status>('loading')

  React.useEffect(() => {
    if (!prestataireId) {
      setStatus('error')
    } else {
      Promise.all([api.users.all(prestataireId), api.societe.all()])
        .then(([userRes, servRes]) => {
          setUsers(userRes)
          AdminStore.actions.setServices(servRes)
          setStatus('fetched')
        })
        .catch((err) => {
          Logger.error(err)
          setStatus('error')
        })
    }
  }, [prestataireId])

  const prestaSites = React.useMemo(
    () => sites.filter((s) => !!users.find((u) => u.siteIds.includes(s.id))),
    [sites, users]
  )

  const sections = React.useMemo(
    () =>
      filterUsers(users, search).reduce(
        (acc, cur) => {
          const isChef = cur.chefEquipe
          acc[isChef ? 'chef' : 'technician'].users.push(cur)
          return acc
        },
        {
          chef: { label: i18n.t('screens.admin.service.chef'), value: 'chef', users: [] },
          technician: { label: i18n.t('screens.admin.service.technician'), value: 'technician', users: [] },
        } as PrestaSections
      ),
    [users, search]
  )

  const contacts = React.useMemo(
    () => [service?.email, ...sections.chef.users.map((c) => c.email)].filter(Boolean),
    [service, sections]
  )

  const tabs: Option[] = [
    { label: i18n.t('screens.admin.service.chef'), value: 'chef', indicator: sections.chef.users.length },
    {
      label: i18n.t('screens.admin.service.technician'),
      value: 'technician',
      indicator: sections.technician.users.length,
    },
  ]

  const [tab, setTab] = React.useState(tabs[0])

  const addUserButton: HeaderButton | undefined = canAdd
    ? {
        label: i18n.t('screens.admin.users.buttons.addUser'),
        onClick: () => navigation.push(`/admin/users/form/info?societeId=${prestataireId}`),
      }
    : undefined

  const headerLinks: HeaderLink[] = [
    { label: i18n.t('screens.admin.providers.title'), onClick: () => navigation.push('/admin/providers') },
  ]

  const openInfo = () => {
    Drawer.open({
      render: () => (
        <DrawerContent>
          <DrawerTitle>{i18n.t('screens.admin.service.info')}</DrawerTitle>
          {renderInfo()}
          <CloseButton activeOpacity={0.9} onPress={Drawer.close}>
            <Icon name="close" size={30} color={Theme.colors.primary} />
          </CloseButton>
        </DrawerContent>
      ),
      hideTarget: true,
    })
  }

  const renderUserCard = ({ item }: { item: UserResponse }) => (
    <UserContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/users/${item.id}`, { userId: item.id })}>
      <UserCard user={item} />
    </UserContainer>
  )

  const renderSearch = () => (
    <>
      {!isPhone && !!service && <Code>{service.codeSociete}</Code>}
      <SearchContainer>
        <TextInputComponent
          onChange={setSearch}
          searchStyle="onDark"
          placeholder={i18n.t('screens.incidentList.search')}
          left={{ name: 'search' }}
          value={search}
        />
      </SearchContainer>
    </>
  )

  const renderUserList = (items: UserResponse[]) => (
    <FlatList
      data={items}
      renderItem={renderUserCard}
      contentContainerStyle={{
        flexWrap: 'wrap',
        flexDirection: isPhone ? 'column' : 'row',
        marginVertical: 20,
        marginHorizontal: isPhone ? 16 : 25,
        justifyContent: isPhone ? 'center' : 'flex-start',
      }}
      ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.users.empty')}</MissingInfo>}
      showsVerticalScrollIndicator={false}
    />
  )

  const renderSite = (site: SiteResponse) => (
    <SiteContainer key={site.id}>
      <IconContainer>
        <SiteIcon name={site.codeLieu as SiteIconName} width={80} color={Theme.colors.background} />
      </IconContainer>
      <Info>{i18n.t(`screens.site.sites.${site.codeLieu}`)}</Info>
    </SiteContainer>
  )

  const renderContact = (contact?: string) => (
    <ContactContainer key={contact}>
      <Info>{contact}</Info>
    </ContactContainer>
  )

  const renderSection = (section: UserSection) => (
    <>
      <TitleContainer key={section.value}>
        <SectionTitle title={`${section.label} (${section.users.length})`} />
      </TitleContainer>
      {renderUserList(section.users)}
    </>
  )

  const renderInfo = () => {
    return (
      <>
        <SectionTitle title={i18n.t('screens.admin.service.site', { count: prestaSites.length })} />
        <SitesContainer>
          {prestaSites.length > 0 ? prestaSites.map(renderSite) : <Info>{i18n.t('screens.admin.service.noSite')}</Info>}
        </SitesContainer>
        <SectionTitle title={i18n.t('screens.admin.service.contact', { count: contacts.length })} />
        {contacts.length > 0 ? contacts.map(renderContact) : <Info>{i18n.t('screens.admin.service.noContact')}</Info>}
      </>
    )
  }

  const renderUsers = () => {
    if (isPhone) {
      return (
        <>
          <SegmentedContainer>
            <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
          </SegmentedContainer>
          {renderUserList(sections[tab.value as keyof PrestaSections].users)}
        </>
      )
    } else {
      return (
        <ContentContainer>
          <RightContainer>
            {renderSection(sections.chef)}
            {renderSection(sections.technician)}
          </RightContainer>
          <LeftContainer>{renderInfo()}</LeftContainer>
        </ContentContainer>
      )
    }
  }

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? service?.nameSociete || i18n.t('screens.admin.service.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      fixed
      onLeft={navigation.goBack}
      right="prestataire"
      onRight={openInfo}>
      {!!service && status === 'fetched' ? (
        <>
          {!isPhone && (
            <HeaderContainer>
              <Header
                title={service.nameSociete}
                renderBottom={renderSearch}
                buttons={!!addUserButton ? [addUserButton] : []}
                links={headerLinks}
              />
            </HeaderContainer>
          )}

          {renderUsers()}

          {isPhone && !!addUserButton && (
            <BottomContainer>
              <Button status="active" label={addUserButton.label} onClick={addUserButton.onClick} />
            </BottomContainer>
          )}
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}
    </Root>
  )
}

export default AdminServiceScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const UserContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`

const BottomContainer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const Code = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  font-size: 16px;
  margin-top: 8px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const SegmentedContainer = styled(View)`
  padding: 12px 16px 0;
`

const ContentContainer = styled(View)`
  flex-direction: row;
  flex: 1;
`

const RightContainer = styled(ScrollView)`
  flex: 2;
  background-color: ${(props) => props.theme.colors.background};
  padding: 24px 0;
`

const LeftContainer = styled(ScrollView)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 24px;
`

const SitesContainer = styled(View)`
  margin: 16px 0;
`

const SiteContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 16px;
`

const IconContainer = styled(View)`
  height: 40px;
  width: 40px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.accent};
  margin-right: 16px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  font-size: 16px;
`

const TitleContainer = styled(View)`
  margin: 0 24px;
`

const ContactContainer = styled(View)`
  margin-top: 16px;
`

const DrawerContent = styled(ScrollView)`
  padding: 20px;
`

const DrawerTitle = styled(Text)`
  text-align: center;
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 24px;
`

const CloseButton = styled(TouchableOpacity)`
  position: absolute;
  z-index: 2;
  top: -6px;
  left: 0;
  padding: 0;
`
